import { Base } from '@studiometa/js-toolkit';
import { transition } from '@studiometa/js-toolkit/utils';

/**
 * @typedef {object} MenuItemRefs
 * @property {HTMLAnchorElement} link[]
 * @property {HTMLElement} submenu
 */

/**
 * @typedef {object} MenuItemInterface
 * @property {MenuItemRefs} $refs
 */

/**
 * MenuItem component
 */
export default class MenuItem extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'MenuItem',
    components: {
      MenuItem,
    },
    refs: ['submenu', 'link[]', 'back', 'aim'],
    emits: ['click', 'media-update'],
    options: {
      img: String,
      video: String,
      hasSubmenu: Boolean,
    },
  };

  /**
   * Component Styles definitions for each state
   * @type {object} styles to add/remove
   */
  styles = {
    submenu: {
      open: 'is-open',
      closed: 'is-closed',
      active: '',
    },
  };

  /**
   * Should component react on click
   * @returns {boolean}
   */
  get shouldReactOnClick() {
    return (
      'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
    );
  }

  /**
   * Mounted hook
   * @this {MenuItem & MenuItemInterface}
   */
  mounted() {
    // Add accessibility
    if (this.$refs.link && this.$refs.submenu) {
      this.$refs.link.forEach((link) => {
        link.setAttribute('aria-controls', this.$id);
      });
      this.$refs.submenu.setAttribute('id', this.$id);
    }
  }

  /**
   * Mouseenter
   * @param {Event} event
   */
  onMouseenter(event) {
    if (this.shouldReactOnClick) {
      return;
    }

    this.updateMedias();
    this.open();

    if (this.$refs.aim) {
      this.$refs.aim.style.left = `${event.clientX}px`;
      this.$refs.aim.style.top = `${event.clientY}px`;
      this.$refs.aim.style.display = 'block';
    }
  }

  /**
   * Mouseleave
   */
  onMouseleave() {
    if (this.shouldReactOnClick) {
      return;
    }

    this.$refs.aim.style.display = '';
    this.close();
  }

  /**
   * On $refs.link click
   * @param {object} event T
   * event object
   * @this {MenuItem & MenuItemInterface}
   */
  onLinkClick(event) {
    if (!this.shouldReactOnClick) {
      return;
    }

    this.$emit('click');
    this.updateMedias();

    // Avoid to preventDefault on submenu item without subsubmenu items.
    if (this.$refs.submenu && event.target.parentElement.dataset.component) {
      event.preventDefault();
      this.open();
    }
  }

  /**
   * onBackClose
   * @this {MenuItem & MenuItemInterface}
   */
  onBackClick() {
    this.close();
  }

  /**
   * Open
   * @this {MenuItem & MenuItemInterface}
   */
  async open() {
    if (this.isOpen) {
      return;
    }

    this.updateMedias();
    if (!this.$options.hasSubmenu) {
      return;
    }

    this.isOpen = true;

    Object.entries(this.styles)
      .filter(([refName]) => refName === 'root' || this.$refs[refName])
      .forEach(([refName, { open, closed, active }]) => {
        const ref = refName === 'root' ? this.$el : this.$refs[refName];

        if (ref instanceof HTMLElement) {
          transition(
            ref,
            {
              from: closed,
              active,
              to: open,
            },
            'keep'
          );
        }
      });

    if (this.$refs.submenu) {
      this.$refs.submenu.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Close
   * @this {MenuItem & MenuItemInterface}
   */
  close() {
    if (!this.isOpen || !this.$options.hasSubmenu) {
      return;
    }

    this.isOpen = false;

    Object.entries(this.styles)
      .filter(([refName]) => refName === 'root' || this.$refs[refName])
      .forEach(([refName, { open, closed, active }]) => {
        const ref = refName === 'root' ? this.$el : this.$refs[refName];

        if (ref instanceof HTMLElement) {
          transition(
            ref,
            {
              from: open,
              active,
              to: closed,
            },
            'keep'
          );
        }
      });
  }

  /**
   * Update medias.
   */
  updateMedias() {
    if (this.$options.video !== '') {
      this.$emit('media-update', this.$options.video, 'video');
    }

    if (this.$options.img !== '') {
      this.$emit('media-update', this.$options.img, 'img');
    }
  }

  /**
   * Keyboard nav
   * @param {object} props keyed service props.
   * @param {boolean} props.TAB TAB
   * @param {boolean} props.ESC ESC
   * @param {boolean} props.ENTER ENTER
   */
  keyed({ TAB, ESC, ENTER }) {
    const hasFocusElementWithin = this.$el.contains(document.activeElement);

    if (ESC) {
      this.close();
      if (hasFocusElementWithin) {
        document.activeElement.blur();
      }
    }

    if (this.shouldReactOnClick) {
      if (ENTER && hasFocusElementWithin) {
        this.open();
      } else if (ENTER && !hasFocusElementWithin) {
        this.close();
      }
    } else if (TAB && hasFocusElementWithin) {
      this.open();
    } else if (TAB && !hasFocusElementWithin) {
      this.close();
    }
  }
}
